import {supabase} from '../lib/supabase'

export const studentService = {
  // Get students for a course
  async getStudents(courseId) {
    try {
      const {data, error} = await supabase
        .from('students_clomapper')
        .select(`
          *,
          student_marks_clomapper (
            assessment_id,
            marks
          )
        `)
        .eq('course_id', courseId)
        .order('name', {ascending: true})

      if (error) throw error
      return {data, error: null}
    } catch (error) {
      return {data: null, error: error.message}
    }
  },

  // Save students data with improved marks handling
  async saveStudents(courseId, students, validAssessmentIds = []) {
    try {
      console.log('=== STARTING SAVE PROCESS ===')
      console.log('Course ID:', courseId)
      console.log('Students to save:', students.length)
      console.log('Valid assessment IDs:', validAssessmentIds)

      // Step 1: Handle student records (insert/update)
      let processedStudents = []
      
      for (let i = 0; i < students.length; i++) {
        const student = students[i]
        console.log(`Processing student ${i + 1}:`, student.name)
        
        if (student.id && !student.id.toString().startsWith('temp_')) {
          // Update existing student
          const {data, error} = await supabase
            .from('students_clomapper')
            .update({
              matric_id: student.matricId,
              name: student.name,
              class_group: student.class_group || student.classGroup || 'Ungrouped',
              faculty: student.faculty || ''
            })
            .eq('id', student.id)
            .select()
            .single()
          
          if (error) {
            console.error('Error updating student:', error)
            throw error
          }
          processedStudents.push(data)
        } else {
          // Insert new student
          const {data, error} = await supabase
            .from('students_clomapper')
            .insert({
              matric_id: student.matricId,
              name: student.name,
              course_id: courseId,
              class_group: student.class_group || student.classGroup || 'Ungrouped',
              faculty: student.faculty || ''
            })
            .select()
            .single()
          
          if (error) {
            console.error('Error inserting student:', error)
            throw error
          }
          processedStudents.push(data)
        }
      }

      console.log('All students processed:', processedStudents.length)

      // Step 2: Handle marks for each student
      for (let i = 0; i < processedStudents.length; i++) {
        const dbStudent = processedStudents[i]
        const originalStudent = students[i]
        
        console.log(`\n--- Processing marks for ${dbStudent.name} (ID: ${dbStudent.id}) ---`)
        
        const newMarks = originalStudent.marks || {}
        console.log('New marks to process:', newMarks)

        // Get current marks for this student
        const {data: existingMarks, error: fetchError} = await supabase
          .from('student_marks_clomapper')
          .select('assessment_id, marks, id')
          .eq('student_id', dbStudent.id)
        
        if (fetchError) {
          console.error('Error fetching existing marks:', fetchError)
          throw fetchError
        }

        console.log('Existing marks in DB:', existingMarks)
        
        // Create a map of assessment IDs to existing mark records
        const existingMarksMap = {}
        existingMarks?.forEach(mark => {
          existingMarksMap[mark.assessment_id] = mark
        })

        // Process each assessment for this student
        for (const assessmentId of validAssessmentIds) {
          const markValue = newMarks[assessmentId]
          const existingMark = existingMarksMap[assessmentId]
          
          // Case 1: Mark is empty or null and should be deleted
          if (markValue === null || markValue === undefined || markValue === '') {
            if (existingMark) {
              console.log(`Deleting mark for assessment ${assessmentId}`)
              const {error: deleteError} = await supabase
                .from('student_marks_clomapper')
                .delete()
                .eq('id', existingMark.id)
              
              if (deleteError) {
                console.error('Error deleting mark:', deleteError)
                throw deleteError
              }
              
              console.log(`✓ Successfully deleted mark for assessment ${assessmentId}`)
            }
          } 
          // Case 2: Mark has a value and should be inserted/updated
          else {
            const numericMark = parseFloat(markValue)
            if (!isNaN(numericMark)) {
              if (existingMark) {
                // Update existing mark
                console.log(`Updating mark for assessment ${assessmentId}: ${numericMark}`)
                const {error: updateError} = await supabase
                  .from('student_marks_clomapper')
                  .update({marks: numericMark})
                  .eq('id', existingMark.id)
                
                if (updateError) {
                  console.error('Error updating mark:', updateError)
                  throw updateError
                }
                
                console.log(`✓ Successfully updated mark for assessment ${assessmentId}`)
              } else {
                // Insert new mark
                console.log(`Inserting new mark for assessment ${assessmentId}: ${numericMark}`)
                const {error: insertError} = await supabase
                  .from('student_marks_clomapper')
                  .insert({
                    student_id: dbStudent.id,
                    assessment_id: assessmentId,
                    marks: numericMark
                  })
                
                if (insertError) {
                  console.error('Error inserting mark:', insertError)
                  throw insertError
                }
                
                console.log(`✓ Successfully inserted new mark for assessment ${assessmentId}`)
              }
            } else {
              console.log(`✗ Invalid mark value for assessment ${assessmentId}: ${markValue}`)
            }
          }
        }
      }

      console.log('=== SAVE PROCESS COMPLETED SUCCESSFULLY ===')
      return {data: processedStudents, error: null}
    } catch (error) {
      console.error('=== SAVE PROCESS FAILED ===')
      console.error('Error details:', error)
      return {data: null, error: error.message}
    }
  },

  // Update individual student mark
  async updateStudentMark(studentId, assessmentId, marks) {
    try {
      if (marks === null || marks === undefined || marks === '') {
        // Delete the mark if it's empty
        const {error: deleteError} = await supabase
          .from('student_marks_clomapper')
          .delete()
          .eq('student_id', studentId)
          .eq('assessment_id', assessmentId)
        
        if (deleteError) throw deleteError
        return {data: null, error: null}
      } else {
        // Check if mark already exists
        const {data: existingMark} = await supabase
          .from('student_marks_clomapper')
          .select('id')
          .eq('student_id', studentId)
          .eq('assessment_id', assessmentId)
          .maybeSingle()

        if (existingMark) {
          // Update existing mark
          const {data, error} = await supabase
            .from('student_marks_clomapper')
            .update({marks: parseFloat(marks)})
            .eq('id', existingMark.id)
            .select()

          if (error) throw error
          return {data, error: null}
        } else {
          // Insert new mark
          const {data, error} = await supabase
            .from('student_marks_clomapper')
            .insert({
              student_id: studentId,
              assessment_id: assessmentId,
              marks: parseFloat(marks)
            })
            .select()

          if (error) throw error
          return {data, error: null}
        }
      }
    } catch (error) {
      return {data: null, error: error.message}
    }
  },

  // Clear all students data for a course
  async clearAllStudents(courseId) {
    try {
      console.log('=== STARTING DATA CLEAR PROCESS ===')
      console.log('Course ID:', courseId)
      
      // First, get all student IDs for this course
      const {data: students, error: fetchError} = await supabase
        .from('students_clomapper')
        .select('id')
        .eq('course_id', courseId)
      
      if (fetchError) throw fetchError
      
      const studentIds = students.map(s => s.id)
      console.log(`Found ${studentIds.length} students to delete`)
      
      if (studentIds.length > 0) {
        // Delete all marks for these students
        const {error: marksDeleteError} = await supabase
          .from('student_marks_clomapper')
          .delete()
          .in('student_id', studentIds)
        
        if (marksDeleteError) throw marksDeleteError
        console.log('Successfully deleted all marks')
      }
      
      // Delete all students for this course
      const {error: studentsDeleteError} = await supabase
        .from('students_clomapper')
        .delete()
        .eq('course_id', courseId)
      
      if (studentsDeleteError) throw studentsDeleteError
      
      console.log('Successfully deleted all students')
      console.log('=== DATA CLEAR PROCESS COMPLETED SUCCESSFULLY ===')
      
      return {data: true, error: null}
    } catch (error) {
      console.error('=== DATA CLEAR PROCESS FAILED ===')
      console.error('Error details:', error)
      return {data: null, error: error.message}
    }
  },

  // Get failure reports for a course
  async getFailureReports(courseId) {
    try {
      const {data, error} = await supabase
        .from('failure_reports_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .order('created_at', {ascending: true})

      if (error) throw error
      return {data, error: null}
    } catch (error) {
      return {data: null, error: error.message}
    }
  },

  // Save failure reports
  async saveFailureReports(courseId, reports) {
    try {
      // First, delete existing reports for this course
      await supabase
        .from('failure_reports_clomapper')
        .delete()
        .eq('course_id', courseId)

      // Insert new reports
      const reportsData = reports.map(report => ({
        course_id: courseId,
        student_name: report.studentName,
        matric_no: report.matricNo,
        failure_status: report.failureStatus,
        reason: report.reason
      }))

      const {data, error} = await supabase
        .from('failure_reports_clomapper')
        .insert(reportsData)
        .select()

      if (error) throw error
      return {data, error: null}
    } catch (error) {
      return {data: null, error: error.message}
    }
  }
}